import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCalendarAlt, FaSave, FaTimes } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Event form component for creating and editing events
 */
const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState({
    title: '',
    description: '',
    shortDescription: '',
    startDate: '',
    endDate: '',
    location: {
      name: '',
      address: '',
      city: '',
      country: '',
      coordinates: {
        type: 'Point',
        coordinates: [0, 0]
      },
      isVirtual: false,
      virtualLink: ''
    },
    category: 'education',
    targetAudience: ['all'],
    registrationRequired: false,
    registrationLink: '',
    registrationDeadline: '',
    capacity: '',
    organizer: {
      name: '',
      email: '',
      phone: '',
      website: ''
    },
    isFeatured: false,
    isPublished: false,
    tags: ''
  });
  
  // Categories for the dropdown
  const categories = [
    'education',
    'workshop',
    'seminar',
    'conference',
    'webinar',
    'competition',
    'exhibition',
    'fundraiser',
    'other'
  ];
  
  // Audience options
  const audienceOptions = [
    { value: 'parents', label: 'Parents' },
    { value: 'teachers', label: 'Teachers' },
    { value: 'students', label: 'Students' },
    { value: 'school_admins', label: 'School Administrators' },
    { value: 'mentors', label: 'Mentors' },
    { value: 'all', label: 'Everyone' }
  ];
  
  // Fetch event data if editing
  useEffect(() => {
    if (id) {
      const fetchEvent = async () => {
        try {
          setLoading(true);
          const response = await api.get(`/events/${id}`);
          
          // Format dates for form inputs
          const eventData = response.data.data;
          if (eventData.startDate) {
            eventData.startDate = formatDateForInput(eventData.startDate);
          }
          if (eventData.endDate) {
            eventData.endDate = formatDateForInput(eventData.endDate);
          }
          if (eventData.registrationDeadline) {
            eventData.registrationDeadline = formatDateForInput(eventData.registrationDeadline);
          }
          
          // Convert tags array to comma-separated string for the form
          if (eventData.tags && Array.isArray(eventData.tags)) {
            eventData.tags = eventData.tags.join(', ');
          }
          
          setEvent(eventData);
        } catch (error) {
          console.error('Error fetching event:', error);
          showError('Failed to load event');
        } finally {
          setLoading(false);
        }
      };
      
      fetchEvent();
    }
  }, [id, showError]);
  
  // Format date for datetime-local input
  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle nested objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setEvent({
        ...event,
        [parent]: {
          ...event[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      });
    } else if (name === 'targetAudience') {
      // Handle multiple select for target audience
      const selected = Array.from(e.target.selectedOptions, option => option.value);
      setEvent({
        ...event,
        targetAudience: selected
      });
    } else {
      // Handle regular inputs
      setEvent({
        ...event,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Prepare form data
    const formData = { ...event };
    
    // Convert tags from comma-separated string to array
    if (typeof formData.tags === 'string') {
      formData.tags = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    }
    
    // Convert capacity to number
    if (formData.capacity) {
      formData.capacity = parseInt(formData.capacity, 10);
    }
    
    // Add default coordinates for MongoDB geospatial indexing
    if (!formData.location.coordinates) {
      formData.location.coordinates = {
        type: 'Point',
        coordinates: [0, 0] // Default coordinates (longitude, latitude)
      };
    }
    
    try {
      if (id) {
        // Update existing event
        await api.put(`/events/${id}`, formData);
        showSuccess('Event updated successfully');
      } else {
        // Create new event
        await api.post('/events', formData);
        showSuccess('Event created successfully');
      }
      navigate('/admin/events');
    } catch (error) {
      console.error('Error saving event:', error);
      showError(error.response?.data?.message || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center">
          <FaCalendarAlt className="mr-2 text-primary" /> 
          {id ? 'Edit Event' : 'Create New Event'}
        </h1>
        <p className="text-gray-600 mt-1">
          {id ? 'Update an existing event' : 'Create a new event for the platform'}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-soft p-6">
        {/* Event Basic Information */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Event Title*
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                className="form-input w-full rounded-md"
                value={event.title}
                onChange={handleChange}
                placeholder="Enter event title"
              />
            </div>
            
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category*
              </label>
              <select
                id="category"
                name="category"
                required
                className="form-select w-full rounded-md"
                value={event.category}
                onChange={handleChange}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Short Description* (Summary)
              </label>
              <input
                type="text"
                id="shortDescription"
                name="shortDescription"
                required
                className="form-input w-full rounded-md"
                value={event.shortDescription}
                onChange={handleChange}
                placeholder="Brief summary of the event (shown in listings)"
                maxLength="200"
              />
            </div>
            
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Full Description*
              </label>
              <textarea
                id="description"
                name="description"
                required
                rows="6"
                className="form-textarea w-full rounded-md"
                value={event.description}
                onChange={handleChange}
                placeholder="Detailed description of the event"
              ></textarea>
            </div>
          </div>
        </div>
        
        {/* Date and Time */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Date and Time</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date and Time*
              </label>
              <input
                type="datetime-local"
                id="startDate"
                name="startDate"
                required
                className="form-input w-full rounded-md"
                value={event.startDate}
                onChange={handleChange}
              />
            </div>
            
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                End Date and Time*
              </label>
              <input
                type="datetime-local"
                id="endDate"
                name="endDate"
                required
                className="form-input w-full rounded-md"
                value={event.endDate}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        
        {/* Location Information */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Location</h2>
          <div className="mb-4">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="location.isVirtual"
                name="location.isVirtual"
                className="form-checkbox h-5 w-5 text-primary"
                checked={event.location.isVirtual}
                onChange={handleChange}
              />
              <label htmlFor="location.isVirtual" className="ml-2 block text-sm text-gray-700">
                This is an online/virtual event
              </label>
            </div>
          </div>
          
          {event.location.isVirtual ? (
            <div>
              <label htmlFor="location.virtualLink" className="block text-sm font-medium text-gray-700 mb-1">
                Virtual Meeting Link
              </label>
              <input
                type="url"
                id="location.virtualLink"
                name="location.virtualLink"
                className="form-input w-full rounded-md"
                value={event.location.virtualLink}
                onChange={handleChange}
                placeholder="https://meet.google.com/abc-defg-hij"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="location.name" className="block text-sm font-medium text-gray-700 mb-1">
                  Venue Name*
                </label>
                <input
                  type="text"
                  id="location.name"
                  name="location.name"
                  required={!event.location.isVirtual}
                  className="form-input w-full rounded-md"
                  value={event.location.name}
                  onChange={handleChange}
                  placeholder="e.g. Conference Center"
                />
              </div>
              
              <div>
                <label htmlFor="location.address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address*
                </label>
                <input
                  type="text"
                  id="location.address"
                  name="location.address"
                  required={!event.location.isVirtual}
                  className="form-input w-full rounded-md"
                  value={event.location.address}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </div>
              
              <div>
                <label htmlFor="location.city" className="block text-sm font-medium text-gray-700 mb-1">
                  City*
                </label>
                <input
                  type="text"
                  id="location.city"
                  name="location.city"
                  required={!event.location.isVirtual}
                  className="form-input w-full rounded-md"
                  value={event.location.city}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>
              
              <div>
                <label htmlFor="location.country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country*
                </label>
                <input
                  type="text"
                  id="location.country"
                  name="location.country"
                  required={!event.location.isVirtual}
                  className="form-input w-full rounded-md"
                  value={event.location.country}
                  onChange={handleChange}
                  placeholder="Country"
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Registration Details */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Registration</h2>
          <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="registrationRequired"
                name="registrationRequired"
                className="form-checkbox h-5 w-5 text-primary"
                checked={event.registrationRequired}
                onChange={handleChange}
              />
              <label htmlFor="registrationRequired" className="ml-2 block text-sm text-gray-700">
                Registration required for this event
              </label>
            </div>
          </div>
          
          {event.registrationRequired && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="registrationLink" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Link
                </label>
                <input
                  type="url"
                  id="registrationLink"
                  name="registrationLink"
                  className="form-input w-full rounded-md"
                  value={event.registrationLink}
                  onChange={handleChange}
                  placeholder="URL for registration page"
                />
              </div>
              
              <div>
                <label htmlFor="registrationDeadline" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Deadline
                </label>
                <input
                  type="datetime-local"
                  id="registrationDeadline"
                  name="registrationDeadline"
                  className="form-input w-full rounded-md"
                  value={event.registrationDeadline}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
                  Event Capacity
                </label>
                <input
                  type="number"
                  id="capacity"
                  name="capacity"
                  min="1"
                  className="form-input w-full rounded-md"
                  value={event.capacity}
                  onChange={handleChange}
                  placeholder="Maximum number of attendees"
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Organizer Information */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Organizer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="organizer.name" className="block text-sm font-medium text-gray-700 mb-1">
                Organizer Name*
              </label>
              <input
                type="text"
                id="organizer.name"
                name="organizer.name"
                required
                className="form-input w-full rounded-md"
                value={event.organizer.name}
                onChange={handleChange}
                placeholder="Name of organizing person or entity"
              />
            </div>
            
            <div>
              <label htmlFor="organizer.email" className="block text-sm font-medium text-gray-700 mb-1">
                Organizer Email
              </label>
              <input
                type="email"
                id="organizer.email"
                name="organizer.email"
                className="form-input w-full rounded-md"
                value={event.organizer.email}
                onChange={handleChange}
                placeholder="Contact email for inquiries"
              />
            </div>
            
            <div>
              <label htmlFor="organizer.phone" className="block text-sm font-medium text-gray-700 mb-1">
                Organizer Phone
              </label>
              <input
                type="tel"
                id="organizer.phone"
                name="organizer.phone"
                className="form-input w-full rounded-md"
                value={event.organizer.phone}
                onChange={handleChange}
                placeholder="Contact phone number"
              />
            </div>
            
            <div>
              <label htmlFor="organizer.website" className="block text-sm font-medium text-gray-700 mb-1">
                Organizer Website
              </label>
              <input
                type="url"
                id="organizer.website"
                name="organizer.website"
                className="form-input w-full rounded-md"
                value={event.organizer.website}
                onChange={handleChange}
                placeholder="https://www.example.com"
              />
            </div>
          </div>
        </div>
        
        {/* Additional Information */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Additional Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="targetAudience" className="block text-sm font-medium text-gray-700 mb-1">
                Target Audience*
              </label>
              <select
                id="targetAudience"
                name="targetAudience"
                multiple
                required
                size="6"
                className="form-multiselect w-full rounded-md"
                value={event.targetAudience}
                onChange={handleChange}
              >
                {audienceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Hold Ctrl/Cmd to select multiple options
              </p>
            </div>
            
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma separated)
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                className="form-input w-full rounded-md"
                value={event.tags}
                onChange={handleChange}
                placeholder="e.g. workshop, technology, kenya"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tags help with searching and categorizing
              </p>
            </div>
          </div>
        </div>
        
        {/* Publishing Options */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Publishing Options</h2>
          <div className="flex space-x-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublished"
                name="isPublished"
                className="form-checkbox h-5 w-5 text-primary"
                checked={event.isPublished}
                onChange={handleChange}
              />
              <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700">
                Publish immediately
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isFeatured"
                name="isFeatured"
                className="form-checkbox h-5 w-5 text-primary"
                checked={event.isFeatured}
                onChange={handleChange}
              />
              <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-700">
                Feature this event
              </label>
            </div>
          </div>
        </div>
        
        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/events')}
            className="btn-secondary flex items-center"
            disabled={submitting}
          >
            <FaTimes className="mr-2" /> Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <LoadingSpinner size="sm" color="white" className="mr-2" /> Saving...
              </>
            ) : (
              <>
                <FaSave className="mr-2" /> {id ? 'Update Event' : 'Create Event'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm; 